import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { appTooltipHelpContentClassName } from '@/lib/ui/tooltip-styles';

/** Shared shell for KPI tiles (API Status reference). */
export const metricWidgetSurface = 'rounded-xl border border-border bg-card text-card-foreground shadow-sm transition-[box-shadow,border-color,background-color] duration-300 ease-soft hover:shadow-lg hover:shadow-slate-900/10 dark:hover:shadow-black/35 hover:border-primary/25';

/** Primary “online / success” value color (matches dashboard API Status). */
export const metricValueSuccessClass = 'text-[#2e7d32] dark:text-green-400';
export const metricValueWarningClass = 'text-amber-600 dark:text-amber-400';
export const metricValueDangerClass = 'text-red-600 dark:text-red-400';
export const metricValueInfoClass = 'text-blue-600 dark:text-blue-400';
export const metricValueVioletClass = 'text-violet-600 dark:text-violet-400';

/** Shared responsive grids for KPI rows (use with `MetricWidget`). */
export const metricWidgetGridClass4 = 'grid grid-cols-1 content-start items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-4';
export const metricWidgetGridClass6 = 'grid grid-cols-2 content-start items-stretch gap-3 sm:grid-cols-3 lg:grid-cols-6';
/**
 * Shift status row: fill available width.
 * 2 → 3 → 5 columns (5 only on xl+ so labels stay readable on ~14″).
 */
export const metricWidgetGridClassShiftStatus = 'grid w-full grid-cols-2 content-start items-stretch gap-3 md:grid-cols-3 xl:grid-cols-5';
export const metricWidgetGridClass5 = 'grid grid-cols-1 content-start items-stretch gap-4 sm:grid-cols-2 xl:grid-cols-5';
export const metricWidgetGridClass2 = 'grid grid-cols-1 content-start items-stretch gap-4 sm:grid-cols-2';
export type MetricIconVariant = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'violet';

/** Value text color matching icon variant (omit for neutral). */
export function metricValueClassForVariant(variant: MetricIconVariant): string | undefined {
  switch (variant) {
    case 'success':
      return metricValueSuccessClass;
    case 'warning':
      return metricValueWarningClass;
    case 'danger':
      return metricValueDangerClass;
    case 'info':
      return metricValueInfoClass;
    case 'violet':
      return metricValueVioletClass;
    default:
      return undefined;
  }
}
export function formatMetricValue(value: number | string): string {
  return typeof value === 'number' ? value.toLocaleString() : value;
}
const metricIconSurface: Record<MetricIconVariant, string> = {
  neutral: 'bg-muted text-muted-foreground',
  success: 'bg-green-100 text-green-700 dark:bg-green-950/80 dark:text-green-400',
  warning: 'bg-amber-100 text-amber-800 dark:bg-amber-950/80 dark:text-amber-400',
  danger: 'bg-red-100 text-red-700 dark:bg-red-950/80 dark:text-red-400',
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-950/80 dark:text-blue-400',
  violet: 'bg-violet-100 text-violet-700 dark:bg-violet-950/80 dark:text-violet-400'
};

/** Colored icon tile for KPI cards (pairs with `valueClassName` on the same variant). */
export function MetricWidgetIcon(t0) {
  const $ = _c(10);
  const {
    icon: Icon,
    variant: t1,
    size: t2,
    className
  } = t0;
  const variant = t1 === undefined ? "neutral" : t1;
  const size = t2 === undefined ? "default" : t2;
  const compact = size === "compact";
  const t3 = compact ? "h-8 w-8" : "h-9 w-9";
  const t4 = metricIconSurface[variant];
  let t5;
  if ($[0] !== className || $[1] !== t3 || $[2] !== t4) {
    t5 = cn("flex shrink-0 items-center justify-center rounded-lg", t3, t4, className);
    $[0] = className;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const t6 = compact ? "h-3.5 w-3.5" : "h-4 w-4";
  let t7;
  if ($[4] !== Icon || $[5] !== t6) {
    t7 = <Icon className={t6} />;
    $[4] = Icon;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t5 || $[8] !== t7) {
    t8 = <span className={t5} aria-hidden={true}>{t7}</span>;
    $[7] = t5;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  return t8;
}
export type StatusDotVariant = 'success' | 'warning' | 'danger' | 'neutral';
const dotClass: Record<StatusDotVariant, string> = {
  success: 'bg-green-600 dark:bg-green-500',
  warning: 'bg-amber-500 dark:bg-amber-400',
  danger: 'bg-red-600 dark:bg-red-500',
  neutral: 'bg-muted-foreground/40'
};
export function StatusDot(t0) {
  const $ = _c(5);
  const {
    variant,
    className
  } = t0;
  const t1 = dotClass[variant];
  let t2;
  if ($[0] !== className || $[1] !== t1) {
    t2 = cn("inline-block h-2.5 w-2.5 shrink-0 rounded-full", t1, className);
    $[0] = className;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <span className={t2} aria-hidden={true} />;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/** Per-metric help — corner placement is default on KPI cards. */
export function MetricWidgetHelp(t0) {
  const $ = _c(15);
  const {
    content,
    ariaLabel,
    placement: t1,
    tooltipSide,
    className
  } = t0;
  const placement = t1 === undefined ? "inline" : t1;
  const side = tooltipSide ?? (placement === "corner" ? "right" : "top");
  const t2 = placement === "corner" ? "absolute left-2 top-2 z-10 flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground/30 transition-[color,background-color,opacity] hover:bg-muted/70 hover:text-muted-foreground focus-visible:text-muted-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-0 group-hover:text-muted-foreground/50" : "inline-flex shrink-0 rounded-full p-0.5 text-muted-foreground/45 transition-colors hover:bg-muted/60 hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1";
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn(t2, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = placement === "corner" ? "h-3 w-3" : "h-3.5 w-3.5";
  let t5;
  if ($[3] !== t4) {
    t5 = <Info className={t4} strokeWidth={2.25} />;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== ariaLabel || $[6] !== t3 || $[7] !== t5) {
    t6 = <TooltipTrigger asChild={true}><button type="button" onClick={_temp} onKeyDown={_temp2} className={t3} aria-label={ariaLabel}>{t5}</button></TooltipTrigger>;
    $[5] = ariaLabel;
    $[6] = t3;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== content || $[10] !== side) {
    t7 = <TooltipContent side={side} sideOffset={8} className={appTooltipHelpContentClassName}>{content}</TooltipContent>;
    $[9] = content;
    $[10] = side;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <Tooltip>{t6}{t7}</Tooltip>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp(e) {
  return e.stopPropagation();
}
export interface MetricWidgetProps {
  title: string;
  /** Short explanation on discreet (i) in the card corner. */
  info?: string;
  infoAriaLabel?: string;
  /** Top-right indicator (e.g. `StatusDot` or a small icon). */
  trailing?: React.ReactNode;
  value: React.ReactNode;
  valueClassName?: string;
  subtitle?: React.ReactNode;
  /** Extra block below subtitle (badges, etc.). */
  footer?: React.ReactNode;
  /** Tighter layout for dense grids (e.g. 6 shift KPIs). */
  density?: 'default' | 'compact';
  /** Opens drill-down or other action; card becomes keyboard-focusable. */
  onClick?: () => void;
  className?: string;
}

/**
 * Compact KPI tile: small semibold title + optional trailing marker, large value, muted subtitle.
 */
export function MetricWidget(t0) {
  const $ = _c(45);
  const {
    title,
    info,
    infoAriaLabel,
    trailing,
    value,
    valueClassName,
    subtitle,
    footer,
    density: t1,
    onClick,
    className
  } = t0;
  const density = t1 === undefined ? "default" : t1;
  const compact = density === "compact";
  const interactive = typeof onClick === "function";
  const t2 = interactive ? "button" : undefined;
  const t3 = interactive ? 0 : undefined;
  const t4 = interactive ? onClick : undefined;
  let t5;
  if ($[0] !== interactive || $[1] !== onClick) {
    t5 = interactive ? e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    } : undefined;
    $[0] = interactive;
    $[1] = onClick;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const t6 = compact ? "p-4" : "p-6";
  const t7 = interactive && "cursor-pointer hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
  let t8;
  if ($[3] !== className || $[4] !== t6 || $[5] !== t7) {
    t8 = cn(metricWidgetSurface, "group relative flex flex-col", t6, t7, className);
    $[3] = className;
    $[4] = t6;
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== info || $[8] !== infoAriaLabel || $[9] !== title) {
    t9 = info ? <MetricWidgetHelp placement="corner" content={info} ariaLabel={infoAriaLabel ?? title} /> : null;
    $[7] = info;
    $[8] = infoAriaLabel;
    $[9] = title;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const t10 = info && "pl-5";
  const t11 = compact ? "line-clamp-2 text-xs font-medium text-muted-foreground" : "truncate text-sm font-semibold text-foreground";
  let t12;
  if ($[11] !== t10 || $[12] !== t11) {
    t12 = cn("min-w-0 flex-1 leading-snug", t10, t11);
    $[11] = t10;
    $[12] = t11;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== t12 || $[15] !== title) {
    t13 = <h3 className={t12}>{title}</h3>;
    $[14] = t12;
    $[15] = title;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== trailing) {
    t14 = trailing != null ? <div className="flex shrink-0 items-center">{trailing}</div> : null;
    $[17] = trailing;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== t13 || $[20] !== t14) {
    t15 = <div className="flex items-start justify-between gap-2">{t13}{t14}</div>;
    $[19] = t13;
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const t16 = compact ? "mt-2.5 text-xl tabular-nums" : "mt-4 text-2xl";
  let t17;
  if ($[22] !== t16 || $[23] !== valueClassName) {
    t17 = cn("font-bold tracking-tight text-foreground", t16, valueClassName);
    $[22] = t16;
    $[23] = valueClassName;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== t17 || $[26] !== value) {
    t18 = <div className={t17}>{value}</div>;
    $[25] = t17;
    $[26] = value;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== compact || $[29] !== subtitle) {
    t19 = subtitle != null ? <p className={cn("leading-snug text-muted-foreground", compact ? "mt-1 text-[11px]" : "mt-1.5 text-xs")}>{subtitle}</p> : null;
    $[28] = compact;
    $[29] = subtitle;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== compact || $[32] !== footer) {
    t20 = footer != null ? <div className={cn("space-y-2", compact ? "mt-2" : "mt-3")}>{footer}</div> : null;
    $[31] = compact;
    $[32] = footer;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== t15 || $[35] !== t18 || $[36] !== t19 || $[37] !== t2 || $[38] !== t20 || $[39] !== t3 || $[40] !== t4 || $[41] !== t5 || $[42] !== t8 || $[43] !== t9) {
    t21 = <div role={t2} tabIndex={t3} onClick={t4} onKeyDown={t5} className={t8}>{t9}{t15}{t18}{t19}{t20}</div>;
    $[34] = t15;
    $[35] = t18;
    $[36] = t19;
    $[37] = t2;
    $[38] = t20;
    $[39] = t3;
    $[40] = t4;
    $[41] = t5;
    $[42] = t8;
    $[43] = t9;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  return t21;
}

/** Loading placeholder matching `MetricWidget` layout (icon tile + value + subtitle). */
export function MetricWidgetSkeleton(t0) {
  const $ = _c(7);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn(metricWidgetSurface, "flex flex-col p-6", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex items-start justify-between gap-3"><div className="h-4 w-28 animate-pulse rounded bg-muted" /><div className="h-9 w-9 shrink-0 animate-pulse rounded-lg bg-muted" /></div>;
    t3 = <div className="mt-4 h-8 w-16 animate-pulse rounded bg-muted" />;
    t4 = <div className="mt-2 h-3 max-w-[10rem] animate-pulse rounded bg-muted" />;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1) {
    t5 = <div className={t1} aria-hidden={true}>{t2}{t3}{t4}</div>;
    $[5] = t1;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}

/** Same outer shell as `MetricWidget`, but free-form body (e.g. Record / system status rows). */
export interface PanelWidgetProps {
  title: string;
  trailing?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}
export function PanelWidget(t0) {
  const $ = _c(15);
  const {
    title,
    trailing,
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn(metricWidgetSurface, "flex flex-col p-6", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <h3 className="text-sm font-semibold leading-none text-foreground">{title}</h3>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== trailing) {
    t3 = trailing != null ? <div className="flex shrink-0 items-center text-muted-foreground">{trailing}</div> : null;
    $[4] = trailing;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="flex items-start justify-between gap-3">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== children) {
    t5 = <div className="mt-4">{children}</div>;
    $[9] = children;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t1 || $[12] !== t4 || $[13] !== t5) {
    t6 = <div className={t1}>{t4}{t5}</div>;
    $[11] = t1;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}